import { Fragment } from "react";
import { DefaultLayout as Layout } from "../layouts";
import { StaticImage } from "gatsby-plugin-image";
import TrustPilot from "../optimizely-components/TrustPilot/TrustPilot";
import LoanProductCard from "../components/Card/LoanProductCard";
import { Flex } from "theme-ui";
import Divider from "../optimizely-components/Utilities/Dividers/Divider";
import StatsSectionSlider from "../components/Slider/StatsSectionSlider";
import IconCard from "../components/Card/IconCard";
import AccordionSection from "../optimizely-components/Utilities/Accordion/AccordionSection";
import HeaderTextBox from "../components/Box/HeaderTextBox";
import CardSlider from "../components/Slider/CardSlider";
import LoanStepsSlider from "../components/Slider/LoanStepsSlider";
import { Icon } from "../optimizely-components/Utilities/Icons/Icon";
import HomeHeroParallax from "../blocks/HomeHeroParallax";
import LoanProductsSlider from "../components/Slider/LoanProductsSlider";
import { Link } from "gatsby-link";
import { useSecureLinks } from "../hooks/useSecureLinks";

const metaTitle = `Cash Money Line of Credit, Auto Equity Loan & Payday Loan. Online or in a branch.`;
const metaDescription = `Cash Money offers Lines of Credit, Auto Equity Loans, and Payday Loans conveniently online and in branches. Get the funds you need fast with our easy online application, Canada. Help build your credit with an Auto Equity Loan.`;
const metaKeywords = "line of credit, payday loans, auto equity loans";
/*
const jsonLD: WithContext<Organization> = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Cash Money',
  url: `https://www.cashmoney.ca/`,
  sameAs: [
    `https://www.consumeraffairs.com/finance/cash_money.html`,
    `https://twitter.com/cashmoney`,
    `https://www.facebook.com/cashmoneyca`,
    `https://www.trustpilot.com/review/cashmoney.ca`,
    `https://www.youtube.com/user/ThecashmoneyCo`,
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+1-77-526-6639',
    contactType: 'Customer Service',
  },
}
*/

const faqs = [
  {
    questionText: "Which loans can I get pre-approved for?",
    answerContent:
      "<p>You can see all the online loan options in your province and how much you are pre-approved for with no impact to your score. If you choose to accept a Line of Credit then a hard inquiry will occur which may impact your credit score.<sup>§</sup> To get pre-approved for an Auto Equity Loan you will need to visit your local branch.</p>",
  },
  {
    questionText: "How does a Line of Credit work?",
    answerContent:
      '<p>With a Line of Credit, you’re in control of how you borrow. If approved, you can use the full credit limit amount at one time or borrow what you need as you need it. You only pay interest on the amount you borrow. As you make payments, more of your credit limit becomes available. </p>\r\n<ul style="list-style-type:none;" >\r\n    <li class="mbm plm">1. Complete an application online, over the phone or at a branch</li>\r\n    <li class="mbm plm">2. Verify your income</li>\r\n    <li class="mbm plm">3. Receive a lending decision</li>\r\n    <li class="mbm plm">4. Get your money</li>\r\n    <li class="mbm plm">5. Make scheduled loan payments (make additional payments to save on interest)</li>\r\n    <li class="mbm plm">6. Access available credit by getting additional cash advances (up to your credit limit)</li>\r\n</ul>',
  },
  {
    questionText: "Is it good to accept a Line of Credit?",
    answerContent:
      "It depends on your financial needs and ability to repay the loan. A Line of Credit is a flexible and convenient way to borrow a larger amount of money for a longer time than a Payday Loan. And, you don’t have to borrow all the funds at one time. Instead, you can borrow what you need as you need it.",
  },
  {
    questionText: "How do you get approved for a Line of Credit?",
    answerContent:
      '<p>Simply apply online, by phone, or at a branch location. You will need the following to qualify: </p>\r\n<ul style="list-style-type:disc; color:black;">\r\n    <li class="mbm plm">Age 18 or older</li>\r\n    <li class="mbm plm">Valid ID</li>\r\n    <li class="mbm plm">Steady source of income</li>\r\n    <li class="mbm plm">Open chequing account</li>\r\n    <li class="mbm plm">Phone number</li>\r\n</ul>',
  },
  {
    questionText: "Which credit reporting agency do you report to?",
    answerContent:
      "We report <b>Auto Equity Loan</b> information to Equifax<sup>®</sup> Canada credit reporting agency.",
  },
  {
    questionText:
      "What kind of information is reported to the credit reporting agency?",
    answerContent:
      '<p>Currently, we only report <b>Auto Equity Loan</b> information to the credit reporting agency. This includes, but is not limited to: </p>\r\n<ul style="list-style-type:disc;">\r\n    <li class="mbm plm">Type of loan</li>\r\n    <li class="mbm plm">Original loan amount</li>\r\n    <li class="mbm plm">Date loan was opened</li>\r\n    <li class="mbm plm">Date loan was closed (if applicable)</li>\r\n    <li class="mbm plm">Date of last payment</li>\r\n    <li class="mbm plm">If payment was received on time or late/missed </li>\r\n  <li class="mbm plm">Balances </li>\r\n</ul>',
  },
  {
    questionText: "How can I build my credit with my loan?",
    answerContent:
      "There are multiple factors that affect your credit score. We report <b>Auto Equity Loan</b> payment history to Equifax<sup>®</sup> Canada credit reporting agency so it can help build your credit if used responsibly. One way to help build credit† is by making your payments on time.",
  },
];

const globalDuration = 800;
export const HomePage = (): JSX.Element => {
  const secureLinks = useSecureLinks();

  return (
    <Layout backgroundColor="#fff">
      {/* <div
        sx={{
          width: "100%",
          bg: "#A9D4F1",
          display: "flex",
          py: 20,
          px: 20,
          justifyContent: "center",
          alignItems: "center",
          flexDirection: ["column", null, null, "row"],
        }}
      >
        <svg
          width="37"
          height="37"
          viewBox="0 0 37 37"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          sx={{
            mx: 3,
            width: 40,
            mb: [3, null, null, 0],
          }}
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M18.5 35.1266C27.6826 35.1266 35.1266 27.6826 35.1266 18.5C35.1266 9.31739 27.6826 1.87342 18.5 1.87342C9.31739 1.87342 1.87342 9.31739 1.87342 18.5C1.87342 27.6826 9.31739 35.1266 18.5 35.1266ZM18.5 37C28.7173 37 37 28.7173 37 18.5C37 8.28273 28.7173 0 18.5 0C8.28273 0 0 8.28273 0 18.5C0 28.7173 8.28273 37 18.5 37Z"
            fill="black"
          />
          <path
            d="M20.0306 23.3291H17.0204L16.2041 9H20.8469L20.0306 23.3291ZM16 27.8704C16 27.0994 16.2381 26.5588 16.7143 26.2485C17.2018 25.9288 17.7971 25.769 18.5 25.769C19.1916 25.769 19.7812 25.9288 20.2687 26.2485C20.7562 26.5588 21 27.0994 21 27.8704C21 28.6226 20.7562 29.1679 20.2687 29.5064C19.7812 29.8355 19.1916 30 18.5 30C17.7971 30 17.2018 29.8355 16.7143 29.5064C16.2381 29.1679 16 28.6226 16 27.8704Z"
            fill="black"
          />
        </svg>
        <div
          sx={{
            maxWidth: 1000,
            fontWeight: 600,
            textAlign: ["center", null, null, "left"],
          }}
        >
          Attention: Due to the Canada Post strike, Customers who receive
          printed statements in the mail may experience delays. Contact us at{" "}
          <a href="tel:18775266639" title="1-877-526-6639">
            1-877-526-6639
          </a>{" "}
          to update how you receive your statements.
        </div>
      </div> */}
      {/*<SEO title={metaTitle} description={metaDescription} />*/}
      {/* <div
        sx={{
          width: "100%",
          py: 30,
          px: 20,
          backgroundColor: "#BF3E3E",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <p sx={{ color: "#fff", fontSize: "20px" }}>
          Several locations are currently closed due to weather conditions.
          Please contact your local branch for hours of operations.
        </p>
      </div> */}
      <HomeHeroParallax />
      <HeaderTextBox
        header="Personal Loans Designed for Your Needs"
        text="Apply in minutes for a Cash Money loan and be offered our best loan options for your financial situation."
      />
      <section>
        <CardSlider>
          <LoanProductCard
            title="Line of Credit"
            subtitle="Most Popular"
            button={{
              text: "Get Pre-Approved",
              href: secureLinks.loanApplication,
            }}
            learnMoreLink="line-of-credit/"
            checklist={[
              "Up to $10,000",
              "Borrow what you need, pay only for what you use",
              "Apply once and access available credit anytime, anywhere",
              "Get pre-approved with no impact to your credit score<sup>§</sup>",
            ]}
          />
          <LoanProductCard
            title="Auto Equity Loan"
            subtitle=""
            hoverActive
            button={{ text: "Find a Branch", href: "/find-a-store" }}
            learnMoreLink="auto-equity-loans/"
            checklist={[
              "Up to $10,000",
              "Secure your loan with your vehicle",
              "May qualify for more than a Line of Credit",
              "Help build your credit<sup>†</sup>",
              "Get pre-approved <b>in a branch</b> with no impact to your credit score<sup>§</sup>",
            ]}
          />
          <LoanProductCard
            title="Payday Loan"
            button={{ text: "Get Started", href: secureLinks.loanApplication }}
            learnMoreLink="payday-loans/"
            checklist={[
              "Up to $1,500",
              "Get funding in as little as 15 minutes",
              "Flat fee based on amount borrowed",
              "Get pre-approved with no impact to your credit score<sup>§</sup>",
            ]}
          />
        </CardSlider>
      </section>
      <HeaderTextBox
        header="How to Apply for Your Loan"
        text="We will walk you through our simple loan process. (Swipe Below)"
      />

      <LoanStepsSlider />

      <HeaderTextBox
        header="Why Choose Cash Money for Your Loan?"
        text="Because we make the loan process accessible with quality support."
      />
      <StatsSectionSlider />
      <Divider
        sx={{
          bg: "#DADEE5",
          maxWidth: 1165,
          alignSelf: "center",
        }}
      />
      <div
        data-sal="slide-up"
        data-sal-delay="0"
        data-sal-duration={globalDuration}
        data-sal-easing="easeOutQuart"
        sx={{
          maxWidth: 580,
          alignSelf: "center",
          mt: 5,
          px: 3,
        }}
      >
        <StaticImage
          src="../images/personal-loans/personal-loans-header-img.png"
          alt="Cash Money Help"
          placeholder="none"
          objectFit="contain"
        />
      </div>
      <HeaderTextBox
        header="Why Apply for a Cash Money Quick Loan?"
        text="When you need funds to bridge a financial gap, we make it simple to get your money fast."
      />
      <LoanProductsSlider />
      {/* <BannerCarousel /> */}
      <Flex
        as="section"
        sx={{
          flexDirection: ["column", null, null, "row"],
          justifyContent: ["center", null, null, "space-between"],
          maxWidth: 850,
          width: "100%",
          mt: 5,
          alignSelf: "center",
        }}
      >
        <div
          data-sal={"slide-up"}
          data-sal-delay="0"
          data-sal-duration={globalDuration}
          data-sal-easing="easeOutQuart"
        >
          <IconCard
            icon={
              <Icon
                size={120}
                name="globe"
                color="black"
                alt="location anywhere"
              />
            }
            title="Apply from Anywhere"
            text="<a href='https://secure.cashmoney.ca/LoanApplication/Province/'>Online</a>, <a href='tel:18775266639'>by phone</a>, or <a href='find-a-store/'>in a branch</a>, conveniently start your application process with a few verifiable documents. Then, get a loan decision within minutes."
          />
        </div>
        <div
          sx={{
            height: 250,
            width: 1,
            bg: "black",
            display: ["none", null, null, "block"],
          }}
        />
        <div
          data-sal={"slide-up"}
          data-sal-delay="100"
          data-sal-duration={globalDuration}
          data-sal-easing="easeOutQuart"
        >
          <IconCard
            icon={
              <Icon
                size={120}
                name="fast-stop-watch"
                color="black"
                alt="location anywhere"
              />
            }
            title="Fast Funding"
            text="Get your money in as little as 15 minutes with <em>Interac</em> e-Transfer<sup>®</sup> or through direct deposit to your bank account."
          />
        </div>
      </Flex>

      {/* <div
        data-sal="slide-up"
        data-sal-delay="0"
        data-sal-duration={globalDuration}
        data-sal-easing="easeOutQuart"
        sx={{
          alignSelf: "center",
          my: [2, null, null, 4],
        }}
      >
        <a
          href={secureLinks.loanApplication}
          sx={{
            variant: "buttons.newPrimary",
            mt: "35px",
            alignSelf: "center",
            width: 308,
            mb: [3, null, null, 0],
          }}
        >
          Apply Now
        </a>
      </div> */}

      <HeaderTextBox header="Satisfied Customers" sx={{ mt: 3 }} />
      <TrustPilot />

      <HeaderTextBox
        header="Frequently Asked Questions"
        text={
          <p sx={{ pt: 2 }}>
            Want more answers? Check out our{" "}
            <Link to="/faqs" sx={{ textDecoration: "underline" }}>
              {" "}
              FAQs{" "}
            </Link>{" "}
            page.
          </p>
        }
        sx={{
          pb: [3, null, null, 4],
        }}
      />

      <AccordionSection
        faqQuestions={faqs}
        sx={{
          pt: 0,
        }}
      />
      <br />
    </Layout>
  );
};

export default HomePage;

export function Head() {
  return (
    <Fragment>
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
    </Fragment>
  );
}
